import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useI18n } from "@/lib/i18n";
import { api } from "@/lib/api";
import "./pixo.css";

/* Batch 17 helpers: the Holo Shard icon, wallet chip + log, shard bursts, map-piece dots. */

export function ShardIcon({ className = "eco-shard", style }) {
  return (
    <svg className={className} style={style} viewBox="0 0 10 12" shapeRendering="crispEdges" aria-hidden>
      <defs>
        <linearGradient id="ecoHg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#7DD3FC" />
          <stop offset=".5" stopColor="#B583FF" />
          <stop offset="1" stopColor="#F9A8D4" />
        </linearGradient>
      </defs>
      <path fill="#0A0A0C" d="M4 0h2v1h1v2h1v2h1v4H8v1H7v1H6v1H4v-1H3v-1H2V9H1V5h1V3h1V1h1z" />
      <path fill="url(#ecoHg)" d="M4 1h2v2h1v2h1v4H7v1H6v1H4v-1H3V9H2V5h1V3h1z" />
      <path fill="#fff" d="M4 2h1v2H4zM3 5h1v1H3z" />
    </svg>
  );
}

/** Tell every wallet chip on the page about a new balance. */
export function emitShards(n) {
  if (typeof n === "number") window.dispatchEvent(new CustomEvent("tier:shards", { detail: n }));
}

/** Little shards flying out of an element (purchases, sales, breaking duplicates). */
export function shardBurst(el, n = 10) {
  if (!el || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
  const r = el.getBoundingClientRect();
  const b = document.createElement("div");
  b.className = "eco-burst";
  b.style.left = `${r.left + r.width / 2}px`;
  b.style.top = `${r.top}px`;
  for (let i = 0; i < n; i++) {
    const a = Math.random() * Math.PI * 2;
    const d = 40 + Math.random() * 50;
    b.insertAdjacentHTML(
      "beforeend",
      `<svg viewBox="0 0 10 12" style="--dx:${Math.cos(a) * d}px;--dy:${Math.sin(a) * d - 30}px;--r:${Math.random() * 360}deg"><path fill="#0A0A0C" d="M4 0h2v1h1v2h1v2h1v4H8v1H7v1H6v1H4v-1H3v-1H2V9H1V5h1V3h1V1h1z"/><path fill="#B583FF" d="M4 1h2v2h1v2h1v4H7v1H6v1H4v-1H3V9H2V5h1V3h1z"/></svg>`
    );
  }
  document.body.appendChild(b);
  setTimeout(() => b.remove(), 1000);
}

// 3x5 pixel digits (no font needed): each row is 3 bits
const DIGITS = {
  0: ["111", "101", "101", "101", "111"], 1: ["010", "110", "010", "010", "111"], 2: ["111", "001", "111", "100", "111"],
  3: ["111", "001", "111", "001", "111"], 4: ["101", "101", "111", "001", "001"], 5: ["111", "100", "111", "001", "111"],
  6: ["111", "100", "111", "101", "111"], 7: ["111", "001", "010", "010", "010"], 8: ["111", "101", "111", "101", "111"],
  9: ["111", "101", "111", "001", "111"],
};
/** A number drawn in pixel digits (crisp at any size). `px` = size of one pixel in CSS px. */
export function PixelNumber({ value, px = 3, color = "#F5EEDC", shadow = true }) {
  const str = String(Math.max(0, Math.floor(Number(value) || 0)));
  const cells = [];
  [...str].forEach((ch, i) => {
    (DIGITS[ch] || DIGITS[0]).forEach((row, y) =>
      [...row].forEach((b, x) => b === "1" && cells.push([i * 4 + x, y]))
    );
  });
  const w = str.length * 4 - 1;
  return (
    <svg width={w * px + (shadow ? px : 0)} height={5 * px + (shadow ? px : 0)} viewBox={`0 0 ${w + (shadow ? 1 : 0)} ${5 + (shadow ? 1 : 0)}`} shapeRendering="crispEdges" role="img" aria-label={str} style={{ display: "inline-block", verticalAlign: "middle" }}>
      {shadow && cells.map(([x, y], i) => <rect key={`s${i}`} x={x + 1} y={y + 1} width="1" height="1" fill="#0A0A0C" />)}
      {cells.map(([x, y], i) => <rect key={i} x={x} y={y} width="1" height="1" fill={color} />)}
    </svg>
  );
}

/** Owner-only wallet square for the Pixie panel. Tapping it shows the balance (pixel numbers), "Holo Shards" in holo letters and a way to the Shop. */
export function WalletButton({ initial = 0, className = "", variant = "row", testid }) {
  const { t } = useI18n();
  const E = t.eco;
  const [bal, setBal] = useState(initial);
  const [open, setOpen] = useState(false);

  useEffect(() => setBal(initial), [initial]);
  useEffect(() => {
    const on = (e) => setBal(e.detail);
    window.addEventListener("tier:shards", on);
    return () => window.removeEventListener("tier:shards", on);
  }, []);

  return (
    <>
      {variant === "chip" ? (
        // batch 22 · small "💎 N HOLO SHARDS" box (profile, under the level; inventory header)
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid={testid || "wallet-chip"}
          aria-label={`${bal} Holo Shards`}
          className={`eco-wallet eco-wallet-chip ${className}`}
        >
          <ShardIcon style={{ width: 11, height: 13 }} />
          <PixelNumber value={bal} px={1.4} color="#ddd6fe" shadow={false} />
          {/* patch 24: capitals sit high in their line box; nudge them so they line up with the number */}
          <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-violet-100/90 leading-none translate-y-[0.5px]">Holo Shards</span>
        </button>
      ) : (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={testid || "wallet-chip"}
        aria-label={`${bal} ${E.shards}`}
        className={`eco-wallet-btn ${className}`}
      >
        <span className="w-7 h-7 shrink-0 grid place-items-center">
          <ShardIcon className="" style={{ width: 20, height: 24 }} />
        </span>
        <span className="flex items-center gap-1.5 min-w-0 text-[10px] font-bold uppercase tracking-[0.14em] text-violet-100 whitespace-nowrap">
          {E.wallet}
          <span className="text-violet-300/70">·</span>
          <PixelNumber value={bal} px={1.4} color="#ddd6fe" shadow={false} />
        </span>
      </button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className="max-w-sm bg-[#16161b] border border-violet-400/30 rounded-sm sm:rounded-sm text-stone-100 p-6" data-testid="wallet-modal">
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-amber-400/90">{E.wallet}</div>
          <DialogTitle className="flex items-center gap-3 py-1">
            <ShardIcon style={{ width: 30, height: 36 }} />
            <PixelNumber value={bal} px={6} />
          </DialogTitle>
          <div className="eco-holo-text font-display text-[34px] font-black uppercase leading-none -mt-1" data-testid="wallet-holo">Holo Shards</div>
          <DialogDescription className="text-xs text-stone-400">{E.wallet_desc}</DialogDescription>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              setTimeout(() => window.dispatchEvent(new CustomEvent("tier:open-shop")), 150);
            }}
            data-testid="wallet-to-shop"
            className="justify-self-start border border-violet-400/55 bg-violet-500/[0.08] text-violet-100 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.18em] rounded-sm hover:bg-violet-500/20"
          >
            {E.to_shop}
          </button>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Batch 22 · the owner's wallet under their level on the profile (hidden until the wallet unlocks). */
export function ProfileWallet({ className = "" }) {
  const [st, setSt] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/pixo/me").then(({ data }) => alive && setSt(data)).catch(() => {});
    return () => {
      alive = false;
    };
  }, []);
  if (!st || (st.unlocks && !st.unlocks.wallet)) return null;
  return <WalletButton variant="chip" initial={st.shards || 0} className={className} testid="profile-wallet" />;
}

/**
 * Batch 21 · "sell in context": a one-tap shop offer shown right where it's needed
 * (Ducky out of sales → Duck Ticket; out of bait → 3 baits). Hidden when today's limit is used.
 */
export function QuickBuy({ item, onState, onDone, testid }) {
  const { t } = useI18n();
  const [offer, setOffer] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let alive = true;
    import("@/lib/api").then(({ api }) =>
      api
        .get("/pixo/shop")
        .then(({ data }) => {
          if (!alive) return;
          const it = (data.always || []).find((x) => x.key === item);
          setOffer(it && it.left > 0 ? { ...it, balance: data.shards ?? data.balance } : null);
        })
        .catch(() => {})
    );
    return () => {
      alive = false;
    };
  }, [item]);
  if (!offer) return null;
  const Q = t.eco.quick || {};
  const poor = typeof offer.balance === "number" && offer.balance < offer.price;
  async function buy(e) {
    if (busy || poor) return;
    const el = e.currentTarget;
    setBusy(true);
    try {
      const { api } = await import("@/lib/api");
      const { data } = await api.post("/pixo/shop/buy", { item });
      if (data.state) onState?.(() => data.state);
      if (typeof data.state?.shards === "number") emitShards(data.state.shards);
      shardBurst(el, 6);
      setOffer(null);
      onDone?.(data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      const { toast } = await import("sonner");
      toast.error(d === "Not enough shards" ? Q.poor : typeof d === "string" ? d : t.pixo.fish.failed);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="flex items-center justify-between gap-3 border border-violet-400/40 bg-violet-500/[0.07] px-3 py-2.5 rounded-sm" data-testid={testid || `quickbuy-${item}`}>
      <div className="min-w-0">
        <div className="text-sm font-bold text-stone-100">{Q[item]?.title}</div>
        <div className="text-xs text-stone-400">{poor ? Q.poor : Q[item]?.sub}</div>
      </div>
      <button
        type="button"
        onClick={buy}
        disabled={busy || poor}
        className="shrink-0 inline-flex items-center gap-1 border border-violet-400/60 bg-violet-500/15 text-violet-50 font-bold text-[12px] px-2.5 py-1.5 rounded-sm hover:bg-violet-500/25 disabled:opacity-40"
      >
        {Q.buy} <ShardIcon />
        {offer.price}
      </button>
    </div>
  );
}
